import React, { useEffect, useRef, useState } from 'react';

interface ResultHeroProps {
  typeName: string;
  typeDescription: string;
  recommendation: string;
  rarityPercent: number;
}

export default function ResultHero({
  typeName,
  typeDescription,
  recommendation,
  rarityPercent,
}: ResultHeroProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden bg-navy px-5 pt-12 pb-10 md:px-8 md:pt-16 md:pb-14"
    >
      {/* subtle geometric decoration */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]">
        <div className="absolute top-[-40px] right-[-40px] h-40 w-40 rounded-full border border-cream" />
        <div className="absolute bottom-[-20px] left-[10%] h-24 w-24 rounded-full border border-cream" />
        <div className="absolute top-[30%] left-[-10px] h-16 w-16 rotate-45 border border-cream" />
      </div>

      <div
        className={`relative mx-auto max-w-[480px] transition-all duration-700 md:max-w-[700px] ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {/* label */}
        <div className="mb-5 flex items-center gap-2">
          <div className="h-px flex-1 bg-cream/20" />
          <span className="text-[11px] font-medium tracking-widest text-cream/60 uppercase">
            당신의 예복 성향
          </span>
          <div className="h-px flex-1 bg-cream/20" />
        </div>

        {/* type name */}
        <h1 className="mb-3 text-center text-[28px] font-semibold leading-tight tracking-tight text-cream md:text-[34px]">
          &ldquo;{typeName}&rdquo;
        </h1>

        <p className="mb-6 text-center text-[13px] leading-relaxed text-cream/70 md:text-[14px]">
          {typeDescription}
        </p>

        {/* recommendation badge */}
        <div className="mb-5 flex items-center justify-center gap-2">
          <span className="text-[12px] text-cream/50">추천 방향</span>
          <span className="rounded-full bg-softgold/15 px-3 py-1 text-[13px] font-semibold text-softgold">
            {recommendation}
          </span>
        </div>

        {/* rarity */}
        <div className="flex items-center justify-center gap-1.5">
          <div className="flex items-center gap-1">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`h-1.5 w-1.5 rounded-full ${
                  i < 3 ? 'bg-softgold' : 'bg-cream/20'
                }`}
              />
            ))}
          </div>
          <span className="text-[11px] text-cream/50">
            전체 사용자 중 {rarityPercent}%가 이 유형
          </span>
        </div>
      </div>
    </section>
  );
}